import { Check, ChevronDown, Search } from "lucide-react";
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";

import { cn } from "../lib/cn";

export interface PickerOption<T extends string> {
  value: T;
  label: string;
  /** A second line under the label — a cage's bird count, a season's year. */
  hint?: string;
}

export interface PickerProps<T extends string> {
  value: T;
  options: readonly PickerOption<T>[];
  onChange: (value: T) => void;
  /** The accessible name. The visible label above the control is separate. */
  label: string;
  id?: string;
  /** Shown on the trigger when the current value matches no option. */
  placeholder?: string;
  /**
   * Shown on the trigger when there is nothing to choose. The control disables
   * itself in that case, so this is an explanation rather than an invitation.
   */
  emptyLabel?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Past this many options the menu grows a filter field.
 *
 * Fifteen because that is roughly where scrolling stops being a glance and starts being
 * a search: a breeder with a dozen cages reads the list, and one with sixty is looking
 * for "B-12" and should be able to say so. Below the threshold the field is not merely
 * unnecessary, it is in the way — it costs a row of menu height and, on a phone, invites
 * a keyboard over a list that was three items long.
 */
const SEARCH_THRESHOLD = 15;

/** How much of the menu must fit below the trigger before it opens downwards. */
const MIN_ROOM_BELOW = 240;

/**
 * Normalised for matching: case-folded, and accents stripped so a Swedish or German
 * breeder typing `ostra` still finds `Östra`. Ring codes and cage codes are ASCII, but
 * the names beside them are not.
 */
function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * The app's dropdown.
 *
 * Not a native `select`: its menu is drawn by the operating system and cannot be
 * styled at all, so on a screen built out of 28px panels and sage fills it arrives as
 * a grey system list. This renders the menu itself — same panel, same radius, same
 * accent on the chosen row — at the cost of reimplementing the listbox keyboard
 * contract, which is what the ARIA pattern below is.
 *
 * Anchored under its trigger rather than opened as a bottom sheet, because several of
 * these already live *inside* a sheet and a dialog inside a dialog is a stacking
 * problem nobody needs. It flips above the trigger when there is not enough room
 * below — a cage picker near the bottom of a form would otherwise open off-screen,
 * which on a phone is indistinguishable from not opening at all.
 *
 * An empty picker disables itself. A control that opens onto nothing reads as broken;
 * one that is visibly disabled and says why reads as "there is nothing here yet".
 *
 * The visible label beside one of these must NOT carry `htmlFor`. A label pointing at
 * a button *activates* it, so clicking the word "Species" would open the menu — which
 * is right for a native `select` and wrong here. `label` below is the accessible name
 * instead, so nothing is lost by leaving the association off.
 */
export function Picker<T extends string>({
  value,
  options,
  onChange,
  label,
  id,
  placeholder = "Choose…",
  emptyLabel = "Nothing to choose yet",
  disabled = false,
  className,
}: PickerProps<T>) {
  const generatedId = useId();
  const listId = useId();
  const triggerId = id ?? generatedId;

  const wrapper = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const search = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [scrolls, setScrolls] = useState(false);
  const [query, setQuery] = useState("");
  const [dropUp, setDropUp] = useState(false);

  /*
   * An option whose value is the empty string is the "none" choice — "Not placed",
   * "Not a mule". It is a real answer, but it is not something to choose *between*,
   * so a picker offering only that one is empty.
   */
  const choices = options.filter((option) => option.value !== "");
  const isEmpty = choices.length === 0;
  const isDisabled = disabled || isEmpty;
  const searchable = options.length > SEARCH_THRESHOLD;

  const selected = options.find((option) => option.value === value);

  /*
   * What the menu is actually showing. Every index below — `active`, the option ids,
   * the row the arrow keys move through — refers to THIS list and not to `options`,
   * because with a filter applied the two no longer line up and a keyboard that moved
   * through the unfiltered set would select a row nobody could see.
   *
   * Matched on the hint as well as the label: a cage's hint carries the aviary and the
   * row, which is often what a breeder actually remembers about it.
   */
  const visible = useMemo(() => {
    const needle = fold(query.trim());

    if (!needle) {
      return options;
    }

    return options.filter(
      (option) =>
        fold(option.label).includes(needle) || fold(option.hint ?? "").includes(needle),
    );
  }, [options, query]);

  const close = useCallback((): void => {
    setOpen(false);
    setQuery("");
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: PointerEvent): void {
      if (!wrapper.current?.contains(event.target as Node)) {
        close();
      }
    }

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, close]);

  /*
   * Whether a scrollbar is actually there, so the rows can stand clear of it.
   *
   * Measured rather than guessed: it depends on how many options there are against
   * the menu's own maximum height. Safe to feed back into the rows' padding because
   * every label truncates — a narrower row cannot wrap, so it cannot change the
   * height that decided the answer, and the two cannot oscillate.
   */
  useEffect(() => {
    const element = list.current;

    if (!open || !element) {
      return;
    }

    function measure(): void {
      setScrolls(element!.scrollHeight > element!.clientHeight);
    }

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [open, visible.length]);

  /*
   * Which side of the trigger the menu opens on, decided before the browser paints so
   * it never appears below and jump above. Read off the trigger rather than the panel
   * because the panel does not exist yet at this point.
   */
  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const trigger = wrapper.current?.firstElementChild;

    if (!trigger) {
      return;
    }

    const box = trigger.getBoundingClientRect();
    const below = window.innerHeight - box.bottom;

    // Only flip when flipping actually helps. In a short viewport with the field in the
    // middle, neither side has room and downwards is the less surprising of the two.
    setDropUp(below < MIN_ROOM_BELOW && box.top > below);
  }, [open]);

  /*
   * Keep the highlighted row on screen. Arrow keys walk past the fold otherwise, and a
   * filter that leaves one match far down the list would show an empty menu.
   */
  useEffect(() => {
    if (!open) {
      return;
    }

    // By index rather than by id: `useId` emits characters that need CSS escaping, and
    // when the list has rows at all its children line up with `visible` one for one.
    const row = list.current?.children[active];

    // jsdom has no `scrollIntoView`. Optional so a component test is not a crash.
    row?.scrollIntoView?.({ block: "nearest" });
  }, [open, active, query]);

  /*
   * Focus the filter field on a mouse, leave it alone on a touch screen.
   *
   * Autofocus is right on a desktop — the menu opens and typing narrows it. On a phone
   * it raises the on-screen keyboard, which covers the list the breeder just asked to
   * see; there the field is something you tap when scrolling is not finding it.
   */
  useEffect(() => {
    if (!open || !searchable) {
      return;
    }

    if (window.matchMedia?.("(pointer: coarse)").matches) {
      return;
    }

    search.current?.focus();
  }, [open, searchable]);

  function openMenu(): void {
    const index = visible.findIndex((option) => option.value === value);

    setActive(index >= 0 ? index : 0);
    setQuery("");
    setOpen(true);
  }

  function choose(index: number): void {
    const option = visible[index];

    if (option) {
      onChange(option.value);
    }

    close();
  }

  /**
   * The listbox keyboard contract, shared by the trigger and the filter field so both
   * drive the same highlight. Only the focused one receives these, so there is no
   * question of the two disagreeing.
   */
  function handleKeyDown(event: React.KeyboardEvent): void {
    if (isDisabled) {
      return;
    }

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault();
        const step = event.key === "ArrowDown" ? 1 : -1;

        if (!open) {
          openMenu();
        } else {
          setActive((current) => Math.max(0, Math.min(visible.length - 1, current + step)));
        }

        break;
      }

      case "Home":
      case "End": {
        if (open) {
          event.preventDefault();
          setActive(event.key === "Home" ? 0 : Math.max(0, visible.length - 1));
        }

        break;
      }

      case "Enter": {
        event.preventDefault();

        if (open) {
          choose(active);
        } else {
          openMenu();
        }

        break;
      }

      case " ": {
        // Space opens the menu, but inside the filter field it is a character — a cage
        // called "Row B" is unreachable otherwise.
        if (event.currentTarget !== search.current) {
          event.preventDefault();

          if (open) {
            choose(active);
          } else {
            openMenu();
          }
        }

        break;
      }

      case "Escape": {
        if (open) {
          event.preventDefault();
          close();
          // Back to the trigger: Escape means "I am done here", and leaving focus on a
          // field that no longer exists drops it to the body.
          (wrapper.current?.firstElementChild as HTMLElement | null)?.focus();
        }

        break;
      }

      case "Tab": {
        close();
        break;
      }

      default:
        break;
    }
  }

  return (
    <div ref={wrapper} className={cn("relative", className)}>
      <button
        type="button"
        id={triggerId}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && !searchable ? `${listId}-${active}` : undefined}
        aria-label={label}
        disabled={isDisabled}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex h-13.5 w-full items-center justify-between gap-md rounded-control px-5",
          "bg-surface-container-lowest text-left text-field font-normal text-on-surface shadow-card",
          "outline-none transition-shadow",
          "focus-visible:shadow-[inset_0_0_0_2.75px_var(--color-action)]",
          "disabled:cursor-not-allowed disabled:opacity-45",
        )}
      >
        <span className="truncate">
          {isEmpty ? emptyLabel : (selected?.label ?? placeholder)}
        </span>

        <ChevronDown
          aria-hidden
          className={cn(
            "size-4.5 shrink-0 text-on-surface-variant transition-transform",
            open ? "rotate-180" : "",
          )}
        />
      </button>

      {open && !isDisabled ? (
        /*
         * Two elements, not one: the panel carries the radius and the padding, and
         * the list scrolls *inside* it. With the scroll box on the panel itself the
         * scrollbar is laid out flush to the border, and its square ends stick out
         * past a 28px corner.
         */
        <div
          className={cn(
            "absolute inset-x-0 z-30 flex flex-col gap-sm",
            dropUp ? "bottom-full mb-sm" : "top-full mt-sm",
            "rounded-lg bg-surface-container-lowest p-sm shadow-raised",
          )}
        >
          {searchable ? (
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-lg top-1/2 size-4 -translate-y-1/2 text-on-surface-variant"
              />

              <input
                ref={search}
                type="text"
                value={query}
                // Off, every one of them: a menu filter is not a field the browser has
                // ever seen before, and an autofill drawer over the list it is filtering
                // hides the answer the breeder is typing towards.
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                enterKeyHint="done"
                aria-label={`Filter ${label.toLowerCase()}`}
                aria-controls={listId}
                aria-activedescendant={`${listId}-${active}`}
                placeholder="Type to filter…"
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActive(0);
                }}
                onKeyDown={handleKeyDown}
                className={cn(
                  "h-11 w-full rounded-control pl-[calc(var(--spacing-lg)+1.5rem)] pr-lg",
                  "bg-surface-container text-label text-on-surface",
                  "placeholder:text-on-surface-variant",
                  "outline-none transition-shadow",
                  "focus:shadow-[inset_0_0_0_2.75px_var(--color-action)]",
                )}
              />
            </div>
          ) : null}

          <ul
            ref={list}
            id={listId}
            role="listbox"
            aria-label={label}
            className={cn(
              "scrollbar-slim flex max-h-56 flex-col gap-hair overflow-y-auto",
              /*
               * The two rules that make this scroll under a thumb. `touch-pan-y` tells
               * the browser a vertical drag here is a scroll and not a gesture the page
               * might want, and `overscroll-contain` stops a flick that reaches the end
               * of the list from carrying on into the form behind it.
               */
              "touch-pan-y overscroll-contain",
              // Only when the bar is present. Padding a list that does not scroll
              // would leave every row visibly off-centre in its own panel.
              scrolls ? "pr-sm" : "",
            )}
          >
            {visible.length === 0 ? (
              <li className="px-lg py-md text-caption text-on-surface-variant">
                Nothing matches “{query.trim()}”.
              </li>
            ) : null}

            {visible.map((option, index) => {
              const isSelected = option.value === value;

              return (
                <li
                  key={option.value || "__none"}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  /*
                   * Click, not pointer-down.
                   *
                   * Pointer-down fires the instant a finger lands, so the row under it
                   * was chosen before the drag that was meant to scroll the list had
                   * even started — and `preventDefault()` on that event cancels the
                   * scroll itself, so the menu could not be moved at all on a phone.
                   * A click is only synthesised when the finger goes down and up on the
                   * same row, which is exactly the gesture that means "this one".
                   *
                   * The document-level outside-click handler is not a problem here: the
                   * menu is inside `wrapper`, so it never sees these as outside.
                   */
                  onClick={() => choose(index)}
                  // Mouse-down default is what moves focus off the trigger. Suppressed
                  // so the control keeps its focus ring through the whole interaction.
                  onMouseDown={(event) => event.preventDefault()}
                  onPointerEnter={(event) => {
                    // Only a real pointer. A touch fires this on the way to a tap, and
                    // following it would drag the highlight along under the finger.
                    if (event.pointerType === "mouse") {
                      setActive(index);
                    }
                  }}
                  className={cn(
                    "flex min-h-touch cursor-pointer items-center justify-between gap-md",
                    "rounded-inset px-lg py-md transition-colors",
                    /*
                     * Hover and keyboard share one highlight rather than each having
                     * their own: pointer-enter sets the same `active` index the arrow
                     * keys move, so a mouse and a keyboard cannot disagree about which
                     * row is about to be chosen.
                     */
                    isSelected
                      ? index === active
                        ? "bg-primary-fixed-dim text-on-secondary-container"
                        : "bg-secondary-container text-on-secondary-container"
                      : index === active
                        ? "bg-surface-container text-on-surface"
                        : "text-on-surface",
                  )}
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-label">{option.label}</span>
                    {option.hint ? (
                      <span
                        className={cn(
                          "truncate text-caption",
                          isSelected
                            ? "text-on-secondary-container"
                            : "text-on-surface-variant",
                        )}
                      >
                        {option.hint}
                      </span>
                    ) : null}
                  </span>

                  {isSelected ? (
                    <Check className="size-4.5 shrink-0" aria-hidden />
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
